import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { UpdatedForm } from '@core/form-generator-engine/abstractions';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { Mapper } from '@shared/helpers/mapper';
import {
  CreateSecurityRequest,
  EvaluationRequest,
  EvaluationResponse,
  QuestionAnswerEvaluationRequest,
  UpdateSecurityRequest,
} from 'src/app/web-api-client';
import { FormMetadata } from '../../abstractions/form-metadata';
import { SecurityForm } from '../../abstractions/security-form';
import { mapSecurityToCreateSecurityRequest } from '../../mappers';
import { mapSecurityToUpdateSecurityRequest } from '../../mappers/security/map-security-to-update-security';
import { mapToEvaluationRequest } from '../../mappers/security/map-to-evaluation-request';
import { EvaluationTaxResult } from '../../models/evaluation-tax-result';
import { RulesForm } from '../../models/rules-form';
import { RulesFormManager } from '../../services/rules-form-manager';
import { TaxRulesService } from '../../services/tax-rules.service';

@Component({
  standalone: false,
  selector: 'gstar-rules-engine-view',
  templateUrl: './rules-engine-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RulesEngineViewComponent implements OnChanges, OnDestroy {
  private readonly taxRulesService = inject(TaxRulesService);
  private readonly rulesManager = inject(RulesFormManager);

  @Input() formValue: SecurityForm | null = null;
  @Input() securityId!: number;
  @Input() transactionType!: TransactionType;

  @Output() dynamicFormCompleted: EventEmitter<
    CreateSecurityRequest | UpdateSecurityRequest
  > = new EventEmitter();

  @ViewChild('modal', { static: false }) modal: ModalComponent | undefined;

  formSchema: WritableSignal<JSONValue[] | null> = signal(null);
  isFormNotEmpty: WritableSignal<boolean> = signal(true);

  get inProgress(): boolean {
    return this.formSchema() !== null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formValue'].currentValue) {
      const formValue = this.formValue as SecurityForm;
      formValue.transactionType = this.transactionType;

      this.taxRulesService.loadDynamicForm(formValue).subscribe(rulesForm => {
        this.rulesManager.concat(rulesForm);
        this.isFormNotEmpty.set(this.rulesManager.hasQuestions);
        this.formSchema.set([...(this.rulesManager as unknown as JSONValue[])]);

        if (rulesForm.any()) {
          if (this.rulesManager.hasTaxResult()) {
            //This is when there is no any questions but yes has tax results and rules.
            this.generateTaxResult(this.rulesManager.allTaxResult);
          }
        } else {
          // This one is when we want to create a security without rules
          this.generateTaxResult([]);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.rulesManager.clear();
  }

  resetSchema(): void {
    this.formSchema.set(null);
    this.rulesManager.clear();
  }

  updateDynamicForm(updatedForm: UpdatedForm): void {
    if (this.formValue !== null && updatedForm.questionMetadata) {
      this.rulesManager.updateQuestion(updatedForm.updatedQuestion);
      const { ruleId, taxPolicyId } = <FormMetadata>(
        (<unknown>updatedForm.questionMetadata)
      );
      this.getEvaluation(ruleId, taxPolicyId);
    }
  }

  private getEvaluation(taxRuleId: number, taxPolicyId: number): void {
    const evaluationRequest = this.createEvaluationRequest(
      taxPolicyId,
      taxRuleId
    );

    this.taxRulesService
      .execute(evaluationRequest)
      .subscribe((evaluationResponse: EvaluationResponse): void => {
        if (evaluationResponse.usedUnsureAnswer) {
          this.rulesManager.setLastUnsureValue(evaluationResponse);
        }
        this.rulesManager.addEvaluation(evaluationResponse);
        this.evaluate();
        this.formSchema.set([...(this.rulesManager as unknown as JSONValue[])]);
        this.isFormNotEmpty.set(this.rulesManager.hasQuestions);
      });
  }

  private evaluate(): void {
    if (this.rulesManager.hasTaxResult()) {
      this.generateTaxResult(this.rulesManager.allTaxResult);
    } else {
      this.formSchema.set([...(this.rulesManager as unknown as JSONValue[])]);
    }
  }

  private createEvaluationRequest(
    taxPolicyId: number,
    taxRuleId: number
  ): EvaluationRequest {
    const formValue = this.formValue as Partial<SecurityForm>;

    return mapToEvaluationRequest(
      taxPolicyId,
      taxRuleId,
      formValue,
      this.rulesManager.allQuestions
    );
  }

  private generateTaxResult(taxResults: EvaluationTaxResult[]): void {
    const formValue = this.formValue as Partial<SecurityForm>;
    let securityRequest:
      | CreateSecurityRequest
      | UpdateSecurityRequest
      | undefined;

    if (this.securityId) {
      securityRequest = mapSecurityToUpdateSecurityRequest(
        this.securityId,
        taxResults,
        this.rulesManager.allQuestions,
        formValue,
        this.transactionType
      );
    } else {
      securityRequest = mapSecurityToCreateSecurityRequest(
        taxResults,
        this.rulesManager.allQuestions,
        formValue,
        this.transactionType
      );
    }

    if (securityRequest && !this.rulesManager.hasIncompleteForms()) {
      this.dynamicFormCompleted.emit(securityRequest);
    } else {
      this.showNextPage();
    }
  }

  private showNextPage(): void {
    this.rulesManager.next();
    if (this.rulesManager.data) {
      this.taxRulesService
        .evaluate(
          this.rulesManager.current,
          this.formValue as Partial<SecurityForm>,
          Mapper.mapByList(
            this.rulesManager.allQuestions,
            QuestionAnswerEvaluationRequest,
            ['name', 'answer']
          )
        )
        .subscribe((ruleForm: RulesForm) => {
          this.rulesManager.addEvaluation(ruleForm.data!);
          this.evaluate();
        });
    } else {
      if (this.rulesManager.current && this.rulesManager.current.activeRule) {
        const { taxPolicyId, taxRuleId } = this.rulesManager.current.activeRule;
        if (taxPolicyId && taxRuleId) {
          this.getEvaluation(taxRuleId, taxPolicyId);
        }
      }
    }
  }
}
